import React, { useEffect } from 'react';
import useCheckoutState from './hooks/useCheckoutState';
import ChargeStatusMessage from './components/ChargeStatusMessage/ChargeStatusMessage';
import SubscriptionStatusMessage from './components/SubscriptionStatusMessage/SubscriptionStatusMessage';
import { useQuery } from '@tanstack/react-query';
import api from './api';

import CheckoutLoader from './components/CheckoutLoader';
import getSessionId from './lib/getSessionId';
import { submissionKey, paymentKey, subscriptionKey, eventPaymentKey } from './queryKeys';
import getCheckoutPath from './lib/getCheckoutPath';
import getSellerSlug from './services/getSellerSlug';
import getCheckoutSlug from './services/getCheckoutSlug';
import SubmissionStatus from './components/SubmissionStatus';
import { PAGE_TYPES } from './enums/page-types';
import EventConfirmation from './EventConfirmation/EventConfirmation';
import useCheckoutRouter from '@repo/checkout/hooks/useCheckoutRouter';
import getPageTheme from '@repo/checkout/lib/theming/getPageTheme';
import { formatPrice } from './services/formatPrice';
import PoweredByCheckoutPage from './components/PoweredByCheckoutPage';
import shouldShowCheckoutPageBranding from './lib/shouldShowCheckoutPageBranding';
import resolveAffiliateBadgeToken from './lib/resolveAffiliateBadgeToken';
import checkIsVariantActive from './lib/checkIsVariantActive';
import getPriceBillingConfig from './lib/getPriceBillingConfig';
import getOrderBumpOffers from './lib/getOrderBumpOffers';

// for preview, generate a random id
const generateRandomId = () => Math.random().toString(36).substr(2, 9);

export default function CheckoutConfirmation({ paymentId: paymentIdProp }) {
  const router = useCheckoutRouter();

  const {
    checkout,
    product,
    seller,
    theme,
    checkoutType,
    params,
    isTestingPayments,
    isPreview,
    effectiveSelectedPriceId,
  } = useCheckoutState();

  // previewing via After payment > Confirmation
  const isUninteractivePreview = isPreview && !isTestingPayments;

  const enabledPrices = (product?.prices ?? []).filter(p => p?.enabled !== false);
  const previewPrice =
    enabledPrices.find(p => String(p._id) === String(effectiveSelectedPriceId)) ||
    enabledPrices.find(p => String(p._id) === String(product?.defaultPriceId)) ||
    enabledPrices[0] ||
    null;

  const paymentId = isUninteractivePreview ? generateRandomId() : paymentIdProp || params.paymentId;
  const sessionId = isUninteractivePreview ? generateRandomId() : getSessionId();

  const { data: submission, isPending: isLoadingSubmission } = useQuery({
    queryKey: submissionKey(paymentId, sessionId),
    queryFn: () => api.getSubmission(paymentId, sessionId),
    retry: false,
    enabled: checkout.type === PAGE_TYPES.FORM && !isUninteractivePreview,
    ...(isUninteractivePreview &&
      checkout.type === PAGE_TYPES.FORM && {
        initialData: submissionFallbackValues(checkout),
      }),
  });

  const { data: eventPayment, isPending: isLoadingEventPayment } = useQuery({
    queryKey: eventPaymentKey(paymentId, sessionId),
    queryFn: () => api.getCharge(paymentId, sessionId),
    retry: false,
    enabled: checkout.type === PAGE_TYPES.EVENT && !isUninteractivePreview,
    ...(isUninteractivePreview &&
      checkout.type === PAGE_TYPES.EVENT && {
        initialData: eventChargeFallbackValues(checkout),
      }),
  });

  const { data: payment, isPending: isLoadingPayment } = useQuery({
    queryKey: paymentKey(paymentId, sessionId),
    queryFn: () => api.getCharge(paymentId, sessionId),
    retry: false,
    enabled: checkout.type === PAGE_TYPES.CHECKOUT && !isUninteractivePreview,
    ...(isUninteractivePreview &&
      checkout.type === PAGE_TYPES.CHECKOUT && {
        initialData: chargeFallbackValues(checkout, product, previewPrice),
      }),
  });

  const { data: subscription, isPending: isLoadingSubscription } = useQuery({
    queryKey: subscriptionKey(paymentId, sessionId),
    queryFn: () => api.getSubscription(paymentId, sessionId),
    retry: false,
    enabled: checkout.type === PAGE_TYPES.CHECKOUT && !isUninteractivePreview,
    ...(isUninteractivePreview &&
      checkout.type === PAGE_TYPES.CHECKOUT && {
        initialData: subscriptionFallbackValues(checkout, product, previewPrice),
      }),
  });

  // push back to checkout if no submission is found
  useEffect(() => {
    if (
      checkout.type === PAGE_TYPES.FORM &&
      !isLoadingSubmission &&
      !submission &&
      !isUninteractivePreview
    ) {
      const sellerSlug = getSellerSlug(checkout.seller);
      const checkoutSlug = getCheckoutSlug(checkout);
      router.push(getCheckoutPath(sellerSlug, checkoutSlug, checkoutType));
    }
  }, [checkout, isLoadingSubmission, submission, isUninteractivePreview, router, checkoutType]);

  // push back to checkout if no event payment is found
  useEffect(() => {
    if (
      checkout.type === PAGE_TYPES.EVENT &&
      !isLoadingEventPayment &&
      !eventPayment &&
      !isUninteractivePreview
    ) {
      const sellerSlug = getSellerSlug(checkout.seller);
      const checkoutSlug = getCheckoutSlug(checkout);
      router.push(getCheckoutPath(sellerSlug, checkoutSlug, checkoutType));
    }
  }, [checkout, isLoadingEventPayment, eventPayment, isUninteractivePreview, router, checkoutType]);

  // push back to checkout if no payment or subscription is found
  useEffect(() => {
    if (
      checkout.type === PAGE_TYPES.CHECKOUT &&
      !isLoadingPayment &&
      !isLoadingSubscription &&
      !payment &&
      !subscription &&
      !isUninteractivePreview
    ) {
      const sellerSlug = getSellerSlug(checkout.seller);
      const checkoutSlug = getCheckoutSlug(checkout);
      router.push(getCheckoutPath(sellerSlug, checkoutSlug, checkoutType));
    }
  }, [
    checkout,
    isLoadingPayment,
    isLoadingSubscription,
    payment,
    subscription,
    isUninteractivePreview,
    router,
    checkoutType,
  ]);

  const freeBranding = shouldShowCheckoutPageBranding(seller, theme) ? (
    <div className="w-full px-4">
      <PoweredByCheckoutPage
        variant="cta"
        surface="confirmation"
        pageType={checkout.type}
        affiliateToken={resolveAffiliateBadgeToken(seller)}
      />
    </div>
  ) : null;

  if (checkout.type === PAGE_TYPES.FORM) {
    if (isLoadingSubmission) {
      return <CheckoutLoader />;
    }

    if (!submission) {
      return <CheckoutLoader />;
    }

    return (
      <div className="flex flex-col justify-center items-start rounded w-full self-start">
        <SubmissionStatus submission={submission} preview={isPreview} isConfirmation />
        {freeBranding}
      </div>
    );
  } else if (checkout.type === PAGE_TYPES.EVENT) {
    if (isLoadingEventPayment) {
      return <CheckoutLoader />;
    }

    if (!eventPayment) {
      return <CheckoutLoader />;
    }

    return (
      <div className="flex flex-col justify-center items-start rounded w-full self-start">
        <EventConfirmation payment={eventPayment} preview={isPreview} isConfirmation />
        {freeBranding}
      </div>
    );
  } else if (checkout.type === PAGE_TYPES.CHECKOUT) {
    if (isLoadingPayment || isLoadingSubscription) {
      return <CheckoutLoader />;
    }

    if (!payment && !subscription) {
      return <CheckoutLoader />;
    }

    const prices = product?.prices;
    let purchaseIsRecurring;
    if (prices && prices.length > 0) {
      const priceId =
        subscription?.productPriceId ||
        payment?.productPriceId ||
        effectiveSelectedPriceId ||
        product.defaultPriceId;
      const enabledPrices = prices.filter(p => p.enabled !== false);
      const matchedPrice =
        enabledPrices.find(p => String(p._id) === String(priceId)) || enabledPrices[0];
      purchaseIsRecurring = matchedPrice?.billingType === 'recurring';
    } else {
      // TODO(M14): legacy product-scalar fallback — remove once the prices[] backfill migration has run (product.type)
      purchaseIsRecurring = product.type === 'subscription';
    }

    const showSubscription = subscription && (purchaseIsRecurring || !payment);

    return (
      <div className="flex flex-col justify-center items-start rounded w-full self-start">
        {showSubscription ? (
          <SubscriptionStatusMessage
            subscription={subscription}
            email={subscription.customer.email}
            status={subscription.status}
            seller={seller}
            customer={subscription.customer}
            checkout={checkout}
            preview={isPreview}
            isConfirmation
          />
        ) : (
          <ChargeStatusMessage
            status={payment.status}
            email={payment.customer.email}
            customer={payment.customer}
            seller={seller}
            charge={payment}
            checkout={checkout}
            preview={isPreview}
            isConfirmation
          />
        )}

        {freeBranding}
      </div>
    );
  }
}
export const submissionFallbackValues = page => {
  const theme = getPageTheme(page);

  return {
    _id: `{Submission ID}`,
    page: page._id,
    createdAt: new Date(),
    status: 'succeeded',
    livemode: true,
    locale: theme.locale,
    customerEmail: `{Email address}`,
    fields: page.fields?.map(field => ({
      fieldId: field._id,
      label: field.label,
      value: `{${field.label}}`,
    })),
    files: page.files,
  };
};

// TODO(M14): legacy product-scalar fallback — remove once the prices[] backfill migration has run (product.price/setupFee/currency/interval/intervalCount/planIterations)
const getPreviewPricing = (product, price) => {
  const pick = key => (price && price[key] != null ? price[key] : product[key]);
  const config = getPriceBillingConfig(price) || {};
  const pickConfig = key => (price && config[key] != null ? config[key] : product[key]);
  return {
    amount: price ? price.amount : product.price,
    setupFee: pick('setupFee'),
    currency: price?.currency || product.currency,
    interval: pickConfig('interval'),
    intervalCount: pickConfig('intervalCount'),
    planIterations: pickConfig('planIterations'),
  };
};

const subscriptionFallbackValues = (page, product, price) => {
  const theme = getPageTheme(page);
  const pricing = getPreviewPricing(product, price);

  return {
    _id: `{Payment ID}`,
    orderId: `{Order ID}`,
    createdAt: new Date(),
    status: 'active',
    currentPeriodEnd: new Date(),
    sku: product.sku,
    amount: pricing.amount,
    livemode: true,
    interval: pricing.interval,
    intervalCount: pricing.intervalCount,
    planIterations: pricing.planIterations,
    setupFee: pricing.setupFee,
    currency: pricing.currency,
    locale: theme.locale,
    fields: page.fields?.map(field => {
      return {
        fieldId: field._id,
        label: field.label,
        value: `{${field.label}}`,
      };
    }),
    variants: product.variants?.map(variant => ({
      name: variant.name,
      variantId: variant._id,
      selectedOptionName: `{${variant.name}}`,
      sku: `{${variant.name}}`,
    })),
    licenseKey: {
      key: '{License key}',
    },
    customer: {
      loginToken: `{Login token}`,
      email: `{Email address}`,
    },
    paymentMethod: {
      method: 'card',
      gateway: 'stripe',
      brand: 'visa',
      last4: '4242',
    },
    files: product.files,
    coupon: {
      code: '{Coupon code}',
      percentOff: 10,
      amountOff: 1000,
    },
  };
};

export const chargeFallbackValues = (page, product, price) => {
  const theme = getPageTheme(page);
  const pricing = getPreviewPricing(product, price);

  return {
    _id: `{Payment ID}`,
    orderId: `{Order ID}`,
    createdAt: new Date(),
    status: 'paid',
    sku: product.sku,
    amount: pricing.amount,
    amountDue: 0,
    amountPaid: pricing.amount,
    setupFee: pricing.setupFee,
    currency: pricing.currency,
    livemode: true,
    locale: theme.locale,
    fields: page.fields?.map(field => {
      return {
        fieldId: field._id,
        label: field.label,
        value: `{${field.label}}`,
      };
    }),
    variants: product.variants?.filter(checkIsVariantActive).map(variant => {
      const enabledOptions = variant.options.filter(option => option.status === 'enabled');

      return {
        variantId: variant._id,
        name: variant.name,
        quantity: 1,
        ...((variant.selectionType === 'quantity' || variant.selectionType === 'multiple') && {
          selectedOptions: enabledOptions.map(option => ({
            optionId: option._id,
            optionName: option.name,
            additionalChargeAmount: option.additionalChargeAmount,
            originalAdditionalChargeAmount: option.discountedFromPrice,
            quantity: 1,
          })),
        }),
        ...(variant.selectionType === 'single' && {
          selectedOptionName: `{${enabledOptions[0].name}}`,
          additionalChargeAmount: enabledOptions[0].additionalChargeAmount,
          sku: `{${enabledOptions[0].sku}}`,
        }),

        sku: `{${variant.name}}`,
      };
    }),
    licenseKey: {
      key: '{License key}',
    },
    customer: {
      loginToken: `{Login token}`,
      email: `{Email address}`,
    },
    paymentMethod: {
      method: 'card',
      gateway: 'stripe',
      brand: 'visa',
      last4: '4242',
    },
    files: product.files,
    coupon: {
      code: '{Coupon code}',
      discount: (pricing.amount || 0) * 0.1, // 10% of the previewed price
    },
  };
};

export const eventChargeFallbackValues = page => {
  const theme = getPageTheme(page);

  const firstTicketType = page.ticketGroups?.find(
    ticketGroup => ticketGroup.ticketTypes?.length > 0,
  )?.ticketTypes?.[0];

  const currency = page.eventDetails?.currency;
  // Sample money values are braced like every other preview variable so the
  // merchant can tell placeholder from content.
  const sample = amount =>
    `{${formatPrice({ amount, currency, locale: theme.locale, style: 'currency' })}}`;

  return {
    _id: `{Payment ID}`,
    orderId: `{Order ID}`,
    createdAt: new Date(),
    status: 'paid',
    amount: sample(10000),
    amountDue: sample(0),
    amountPaid: sample(10000),
    ticketFeesTotal: sample(firstTicketType?.feeAmount || 1000),
    currency,
    livemode: true,
    locale: theme.locale,
    fields: page.fields?.map(field => {
      return {
        fieldId: field._id,
        label: field.label,
        value: `{${field.label}}`,
      };
    }),
    tickets: firstTicketType
      ? [
          {
            ticketType: firstTicketType._id,
            name: firstTicketType.name,
            quantity: 1,
          },
        ]
      : [],
    orderBumps: getOrderBumpOffers(page, null, page.orderBumpProducts)
      .slice(0, 1)
      .map(offer => ({
        product: offer.productId,
        productTitle: offer.title,
        quantity: 1,
      })),
    customer: {
      loginToken: `{Login token}`,
      email: `{Email address}`,
    },
    paymentMethod: {
      method: 'card',
      gateway: 'stripe',
      brand: 'visa',
      last4: '4242',
    },
    coupon: {
      code: '{Coupon code}',
      discount: sample(1000),
    },
    // The preview never calls the API, so nothing attaches the calendar the way
    // getPayment does for a real booking. Only `event` needs to be truthy for the
    // button to render: the provider links are never followed because the menu is
    // disabled in preview, so the real normalisation stays server-side rather
    // than being duplicated here.
    calendar: page.eventDetails?.startDate
      ? {
          event: {
            title: page.title,
            start: page.eventDetails.startDate,
            end: page.eventDetails.endDate ?? page.eventDetails.startDate,
            timezone: page.eventDetails.timezone,
            location: page.eventDetails.location ?? '',
            description: '',
          },
          icsUrl: null,
        }
      : null,
  };
};
